import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GlassTitle } from '../components/GlassTitle';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/apiErrors';
import { Activity, Eye, EyeOff, ArrowRight, Loader2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { authForgotPassword, authGoogleSession, authResendVerification } from '../services/api.auth';

// Google Icon SVG component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, isAuthenticated, loading: authLoading, setUser, setIsAuthenticated } = useAuth();
  
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [processingOAuth, setProcessingOAuth] = useState(false);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register form state
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [verificationNotice, setVerificationNotice] = useState('');
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);

  // Handle OAuth callback - check for session_id in URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        handleGoogleCallback(sessionId);
      }
    }
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated && !processingOAuth) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, authLoading, navigate, processingOAuth]);

  const handleGoogleCallback = async (sessionId) => {
    setProcessingOAuth(true);
    try {
      const res = await authGoogleSession(sessionId);

      // Store token and user
      localStorage.setItem('token', res.data.access_token);
      
      if (setUser) setUser(res.data.user);
      if (setIsAuthenticated) setIsAuthenticated(true);
      
      toast.success(`Welcome, ${res.data.user.name}!`);
      
      // Clear hash and navigate
      window.history.replaceState(null, '', window.location.pathname);
      navigate('/dashboard');
    } catch (error) {
      console.error('Google OAuth error:', error);
      toast.error('Google login failed. Please try again.');
      window.history.replaceState(null, '', window.location.pathname);
    } finally {
      setProcessingOAuth(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginEmail || !loginPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      setShowResendVerification(false);
      await login(loginEmail, loginPassword);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const message = getApiErrorMessage(error, 'Login failed');
      if (
        error?.response?.status === 403 &&
        typeof message === 'string' &&
        message.toLowerCase().includes('email not verified')
      ) {
        setShowResendVerification(true);
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerEmail || !registerPassword || !confirmPassword) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (registerPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await register(registerEmail, registerPassword, registerName);
      setVerificationNotice(response?.message || 'Account created. Please verify your email before signing in.');
      setLoginEmail(registerEmail.trim().toLowerCase());
      setLoginPassword('');
      setRegisterPassword('');
      setConfirmPassword('');
      setActiveTab('login');
      setShowResendVerification(true);
      if (response?.email_delivery_status === 'failed') {
        toast.error(response?.email_delivery_message || response?.message || 'Account created, but verification email delivery failed.');
      } else {
        toast.success(response?.message || 'Account created. Check your inbox to verify your email before signing in.');
      }
    } catch (error) {
      const message = getApiErrorMessage(error, 'Registration failed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!loginEmail) {
      toast.error('Enter your email address first');
      return;
    }

    setLoading(true);
    try {
      const response = await authResendVerification(loginEmail);
      toast.success(response.data?.message || 'Verification email sent');
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to resend verification email');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!loginEmail) {
      toast.error('Enter your email address first');
      return;
    }

    setLoading(true);
    try {
      const response = await authForgotPassword(loginEmail);
      toast.success(response.data?.message || 'If this email is registered, a reset link has been sent.');
      setForgotPasswordMode(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send password reset email'));
    } finally {
      setLoading(false);
    }
  };

  // Show loading while processing OAuth
  if (processingOAuth) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-zinc-400">Completing sign in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
      
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Activity className="w-8 h-8 text-blue-500" />
            <GlassTitle as="span" size="md" className="text-2xl">TradoPro</GlassTitle>
          </div>
          <p className="text-zinc-500 text-sm">AI-Powered Futures Trading</p>
        </div>

        <Card className="card-gloss border-0">
          <CardHeader className="pb-4">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-zinc-800/50">
                <TabsTrigger 
                  value="login" 
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                  data-testid="login-tab"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger 
                  value="register"
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                  data-testid="register-tab"
                >
                  Register
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          
          <CardContent>
            {/* Google OAuth Button */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 bg-white hover:bg-gray-100 text-gray-800 font-medium border-gray-300 mb-4"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <GoogleIcon />
              <span className="ml-2">Continue with Google</span>
            </Button>

            {/* Divider */}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-zinc-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-zinc-900 px-2 text-zinc-500">Or continue with email</span>
              </div>
            </div>

            {activeTab === 'login' && forgotPasswordMode ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-100">
                  Enter your email and we will send a reset link if the account exists.
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Email
                  </label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 h-11"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Reset Link'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11 border-zinc-700 bg-zinc-900/70 text-zinc-200 hover:bg-zinc-800"
                  onClick={() => setForgotPasswordMode(false)}
                >
                  Back to Sign In
                </Button>
              </form>
            ) : activeTab === 'login' ? (
              <form onSubmit={handleLogin} className="space-y-4">
                {verificationNotice && (
                  <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-100">
                    {verificationNotice}
                  </div>
                )}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Email
                  </label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 h-11"
                    data-testid="login-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 h-11 pr-10"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  className="text-sm text-blue-300 hover:text-blue-200"
                  onClick={() => setForgotPasswordMode(true)}
                >
                  Forgot password?
                </button>

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Sign In with Email
                    </>
                  )}
                </Button>

                {showResendVerification && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 border-zinc-700 bg-zinc-900/70 text-zinc-200 hover:bg-zinc-800"
                    disabled={loading}
                    onClick={handleResendVerification}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Resend Verification Email
                  </Button>
                )}
              </form>
            ) : (
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Name (Optional)
                  </label>
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={registerName}
                    onChange={(e) => setRegisterName(e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 h-11"
                    data-testid="register-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Email
                  </label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={registerEmail}
                    onChange={(e) => setRegisterEmail(e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 h-11"
                    data-testid="register-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 6 characters"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 h-11 pr-10"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Confirm Password
                  </label>
                  <Input
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 h-11"
                    data-testid="register-confirm-password-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-zinc-500 text-center">
                  A verification email will be sent to confirm your account
                </p>
              </form>
            )}

            {/* Placeholder for future social logins */}
            <div className="mt-4 pt-4 border-t border-zinc-800">
              <p className="text-xs text-zinc-600 text-center">
                More sign-in options coming soon: Facebook, Apple
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-zinc-600 text-xs mt-6">
          Secure trading powered by TradoPro
        </p>
      </div>
    </div>
  );
};

export default Login;
